import React, { useState, useEffect } from 'react';
import { ChevronRight, Building2, House, Lock, MapPinned } from 'lucide-react';

const DeviceTree = ({ title, tree, onSelect, expandedPath, defaultOpen = true, icon: CustomIcon }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const hasItems = tree && Object.keys(tree).length > 0;

    useEffect(() => {
        if (expandedPath && hasItems) {
            if (expandedPath.city && tree[expandedPath.city]) {
                setIsOpen(true);
            }
        }
    }, [expandedPath, tree, hasItems]);

    return (
        <div className="border-b border-slate-700">
            <button
                onClick={() => setIsOpen(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 text-slate-300 hover:bg-slate-700/50 transition"
            >
                <span className="text-sm font-semibold flex items-center gap-2">
                    {CustomIcon && <CustomIcon className="w-4 h-4" />}
                    {title}
                </span>
                <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
            </button>
            {isOpen && (
                <div className="px-2 pb-2">
                    {!hasItems ? (
                        <div className="text-slate-500 text-xs px-2 py-4 text-center">Нет устройств</div>
                    ) : (
                        Object.keys(tree).map(city => (
                            <CityNode
                                key={city}
                                city={city}
                                data={tree[city]}
                                onSelect={onSelect}
                                expandedPath={expandedPath}
                                groupName={title}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

const CityNode = ({ city, data, onSelect, expandedPath, groupName }) => {
    const [isOpen, setIsOpen] = useState(false);
    useEffect(() => {
        if (expandedPath && expandedPath.city === city) {
            setIsOpen(true);
        }
    }, [expandedPath, city]);

    return (
        <div className="ml-2">
            <button
                onClick={() => setIsOpen(prev => !prev)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-left text-slate-300 hover:bg-slate-700/50 rounded transition text-sm"
            >
                <ChevronRight className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                <Building2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <span className="font-medium">{city}</span>
            </button>
            {isOpen && (
                <div className="ml-4">
                    {Object.keys(data).map(street => (
                        <StreetNode
                            key={street}
                            city={city}
                            street={street}
                            data={data[street]}
                            onSelect={onSelect}
                            expandedPath={expandedPath}
                            groupName={groupName}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const StreetNode = ({ city, street, data, onSelect, expandedPath, groupName }) => {
    const [isOpen, setIsOpen] = useState(false);
    useEffect(() => {
        if (expandedPath && expandedPath.city === city && expandedPath.street === street) {
            setIsOpen(true);
        }
    }, [expandedPath, city, street]);

    const handleArrowClick = (e) => {
        e.stopPropagation();
        setIsOpen(prev => !prev);
    };

    const handleStreetClick = () => {
        onSelect({ type: 'street', data: { city, street, groupName } });
    };

    return (
        <div className="ml-2">
            <div className="flex items-center gap-1">
                <button onClick={handleArrowClick} className="p-1 text-slate-400 hover:text-white transition" title="Развернуть/свернуть">
                    <ChevronRight className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                </button>
                <button onClick={handleStreetClick} className="flex-1 flex items-center gap-2 px-2 py-1.5 text-left text-slate-300 hover:bg-slate-700/50 rounded transition text-sm">
                    <MapPinned className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <span className="font-medium">{street}</span>
                </button>
            </div>
            {isOpen && (
                <div className="ml-4">
                    {Object.keys(data).map(house => (
                        <HouseNode
                            key={house}
                            city={city}
                            street={street}
                            house={house}
                            devices={data[house]}
                            onSelect={onSelect}
                            expandedPath={expandedPath}
                            groupName={groupName}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const HouseNode = ({ city, street, house, devices, onSelect, expandedPath, groupName }) => {
    const [isOpen, setIsOpen] = useState(false);
    useEffect(() => {
        if (expandedPath && expandedPath.city === city && expandedPath.street === street && expandedPath.house === house) {
            setIsOpen(true);
        }
    }, [expandedPath, city, street, house]);

    const handleArrowClick = (e) => {
        e.stopPropagation();
        setIsOpen(prev => !prev);
    };

    const handleHouseClick = () => {
        onSelect({ type: 'house', data: { city, street, house, groupName } });
    };

    return (
        <div className="ml-2">
            <div className="flex items-center gap-1">
                <button onClick={handleArrowClick} className="p-1 text-slate-400 hover:text-white transition" title="Развернуть/свернуть">
                    <ChevronRight className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                </button>
                <button onClick={handleHouseClick} className="flex-1 flex items-center gap-2 px-2 py-1.5 text-left text-slate-300 hover:bg-slate-700/50 rounded transition text-sm">
                    <House className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span className="font-medium">{house}</span>
                    <span className="text-xs text-slate-500 ml-auto">({devices.length})</span>
                </button>
            </div>
            {isOpen && (
                <div className="ml-4">
                    {devices.map((device, idx) => (
                        <DeviceItem key={idx} device={device} onClick={onSelect} groupName={groupName} />
                    ))}
                </div>
            )}
        </div>
    );
};

const DeviceItem = ({ device, onClick, groupName }) => {
    const statusColor = device.status === 0 ? 'bg-green-500' : device.status === 1 ? 'bg-blue-500' : 'bg-slate-500';
    return (
        <button
            onClick={() => onClick({ type: 'device', data: { ...device, groupName } })}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-slate-400 hover:bg-blue-600/20 hover:text-blue-300 rounded transition text-sm"
        >
            <div className={`w-2 h-2 rounded-full ${statusColor} flex-shrink-0`}></div>
            <span className="truncate">{device.fullName}</span>
            {device.isOplombirovan && (
                <Lock className="w-3.5 h-3.5 ml-auto text-red-400 flex-shrink-0" title="Опломбирован" />
            )}
        </button>
    );
};

export default DeviceTree;